import { Add, Autorenew, CompareArrows, HelpCenterOutlined, Info, Refresh, Remove, RestartAlt } from "@mui/icons-material";
import { alpha, Autocomplete, Box, Button, Container, FormControl, FormLabel, Grid, IconButton, LinearProgress, Paper, Slider, TextField, ToggleButton, Tooltip, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useParams } from "react-router-dom";
import { FormFieldKeyConditionEnum } from "../../_common/enum/forms/fomFieldKeyCondition.enum";
import { FormTemplateTypeEnum } from "../../_common/enum/forms/fomTemplateType.enum";
import { FormFieldKeyActionEnum } from "../../_common/enum/forms/formFieldKeyAction.enum";
import { FormFieldKeyConditionTypeEnum } from "../../_common/enum/forms/formFieldKeyConditionType.enum";
import { FormFieldKeySpecialTypeEnum } from "../../_common/enum/forms/formFieldKeySpecialType.enum";
import { FormFieldValueTypeEnum } from "../../_common/enum/forms/formFieldValueType.enum";
import { MortgageTypeEnum } from "../../_common/enum/mortgageType.enum";
import { ScreenSizeCategoryEnum } from "../../_common/enum/screenSizeCategory.enum";
import { IFormFieldKeyManage } from "../../_common/models/formFieldKey";
import { IFormTemplateSectionFormManage } from "../../_common/models/formTemplateSection/formTemplateSectionFormManage.model";
import { TextValidatorHcs } from "../../_components/textValidator/textValidatorHcs";
import { toastr } from "../../_components/toastr/toastr";
import { getMoneyFormatNoFractions } from "../../_helpers/format.helper";
import { handleErrorResponseAxios } from "../../_helpers/handleResponse.helper";
import { sendScrollMessageToParent } from "../../_helpers/iFrameSize.helper";
import { getWindowSizeCategory, useScreenSize } from "../../_helpers/screenSize.helper";
import { formSectionService } from "../../_services/formSection.service";
import { formTemplateSectionService } from "../../_services/formTemplateSection.service";
import { theme } from "../../_styles/theme.styles";

export function QuoteFiltersSection(params: {
    formTemplateSectionId: number,
    formSectionId?: number,
    isSectionSaving?: boolean,
    isForReview?: boolean,
    onSave: () => void,
    actionId?: FormFieldKeyActionEnum,
    firstName?: string,
    firstNameJointLife?: string | null
}) {
    const signatureRef = useRef(null);

    const [data, setData] = useState<IFormTemplateSectionFormManage>();
    const [screenSizeCategory, setScreenSizeCategory] = useState<ScreenSizeCategoryEnum | null>();
    const [isLoading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const { formId } = useParams<{ formId: string }>();
    const _formId = Number(formId ?? 0);
    const screenSize = useScreenSize();
    const [showMoreFilters, setShowMoreFilters] = useState(true);

    useEffect(() => {
        if (!screenSize) return;
        setScreenSizeCategory(getWindowSizeCategory(screenSize.width));
    }, [screenSize]);

    useEffect(() => {
        fetchTemplateSectionData();
    }, [params.formTemplateSectionId]);

    function fetchTemplateSectionData() {
        if (params.formTemplateSectionId == 0) return;

        setLoading(true);
        return formTemplateSectionService
            .getForm(params.formTemplateSectionId, _formId, params.isForReview, params.actionId)
            .then(
                (json) => {
                    setData(json);
                }
            )
            .finally(() => {
                setLoading(false);
            });
    }

    function handleChange(event: any, ffkId: number) {
        event.preventDefault();

        setData((_data: any) => {
            var __data = { ..._data };

            if (__data.formFieldKeys && __data.formFieldKeys.length) {
                var _formFieldKey = __data.formFieldKeys.find((ffk: IFormFieldKeyManage) => ffk.id === ffkId);

                _formFieldKey.formField.fieldValue = event.target.value;

                if (_formFieldKey.formField) {
                    if (_formFieldKey.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.PropertyValue) {
                        var _loanValue = __data.formFieldKeys.find((ffk: IFormFieldKeyManage) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount);
                        _loanValue.maxValue = _formFieldKey.formField.fieldValue - _formFieldKey.formField.fieldValue * 0.10;
                    }
                }
                else {
                    _formFieldKey.formField = {
                        id: 0,
                        fieldValue: event.target.value,
                        formFieldKeyId: _formFieldKey.id,
                        formTemplateSectionId: _formFieldKey.formTemplateSectionId,
                        formSectionId: params.formSectionId ?? 0
                    };
                }
            }
            return { ..._data } as IFormTemplateSectionFormManage;
        });
    };

    function handleAutocompleteChange(event: any, ffkId: any, value: any) {
        event.preventDefault();
        setData((_data: any) => {
            var __data = { ..._data };

            if (__data.formFieldKeys && __data.formFieldKeys.length) {
                var _formFieldKey = __data.formFieldKeys.find((ffk: IFormFieldKeyManage) => ffk.id === ffkId);

                if (!!_formFieldKey.formField) {
                    _formFieldKey.formField.fieldValue = value
                }

                else {
                    _formFieldKey.formField = {
                        id: 0,
                        fieldValue: value,
                        formFieldKeyId: _formFieldKey.id,
                        formTemplateSectionId: _formFieldKey.formTemplateSectionId,
                        formSectionId: params.formSectionId ?? 0
                    };
                }
            }

            return { ..._data } as IFormTemplateSectionFormManage;
        });
    }

    function submit(event: any) {
        event.preventDefault();

        sendScrollMessageToParent();

        // Logic to limit mortgage cover amount
        if (data && data.formTemplateTypeId === FormTemplateTypeEnum.Mortgages) {

            const incomeFieldValue = Number(data.formFieldKeys?.filter((ffk) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income)[0]?.formField?.fieldValue);
            const coverAmountFieldValue = Number(data.formFieldKeys?.filter((ffk) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount)[0]?.formField?.fieldValue);
            const mortgageTypeFieldValue = data.formFieldKeys?.filter((ffk) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.MortgageType)[0]?.formField?.fieldValue;
            const propertyValueFieldValue = Number(data.formFieldKeys?.filter((ffk) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.PropertyValue)[0]?.formField?.fieldValue);

            if (incomeFieldValue && coverAmountFieldValue && incomeFieldValue * 4 < coverAmountFieldValue) {
                toastr.error(`You can borrow up to 4x your income. Maximum allowed: ${getMoneyFormatNoFractions(incomeFieldValue * 4)}`);
                return;
            }
            if (propertyValueFieldValue && coverAmountFieldValue && mortgageTypeFieldValue !== MortgageTypeEnum.buytolet && coverAmountFieldValue > propertyValueFieldValue * 0.9) {
                toastr.error(`You can borrow up to 90% the property value. Maximum allowed: ${getMoneyFormatNoFractions(propertyValueFieldValue * 0.9)}`);
                return;
            }
            if (propertyValueFieldValue && coverAmountFieldValue && mortgageTypeFieldValue === MortgageTypeEnum.buytolet && coverAmountFieldValue > propertyValueFieldValue * 0.7) {
                toastr.error(`You can borrow up to 70% the property value. Maximum allowed: ${getMoneyFormatNoFractions(propertyValueFieldValue * 0.7)}`);
                return;
            }
        }

        var _formFields: Array<any> = [];
        if (data && data.formFieldKeys && data.formFieldKeys.length) {
            _formFields = data.formFieldKeys.map(ffk => {
                var _formField = ffk.formField || {
                    id: 0,
                    fieldValue: "",
                    fieldValueTest: "",
                    formFieldKeyId: ffk.id,
                    formTemplateSectionId: ffk.formTemplateSectionId,
                    formSectionId: params.formSectionId ?? 0
                }

                if (ffk.formFieldValueTypeId === FormFieldValueTypeEnum.Signature) {
                    _formField.fieldValue = (signatureRef.current as any).getTrimmedCanvas().toDataURL("image/png");
                }

                return _formField;
            });
        }

        var form = {
            id: params.formSectionId ?? 0,
            formId: _formId ?? 0,
            formTemplateSectionId: params.formTemplateSectionId ?? 0,
            formSectionId: params.formSectionId ?? 0,
            formFields: _formFields
        };

        var formData = new FormData();
        formData.append(`id`, form.id.toString());
        formData.append(`formId`, form.formId.toString());
        formData.append(`formTemplateSectionId`, form.formTemplateSectionId.toString());
        formData.append(`formSectionId`, form.formSectionId.toString());

        form.formFields.forEach((formField, index) => {
            for (var formFieldName in formField) {
                var val = (formField as any)[formFieldName];
                formData.append(`formFields[${index}].${formFieldName}`, val || (val === null || val === "" || val === " " ? "" : 0));
            }
        });

        var documentUploadParentFields = document.getElementsByClassName("documentUpload");
        var documentsToClear: Array<string> = []

        if (documentUploadParentFields && documentUploadParentFields.length > 0) {
            var currentIndex = 0;
            Array.from(documentUploadParentFields).forEach((documentUploadParentField: any, index: number) => {

                var fileField = documentUploadParentField.getElementsByClassName("MuiInputBase-input")[0];
                documentsToClear.push(fileField.id);

                var id = fileField.id.split("_")[1];

                if (data && data.formFieldKeys && data.formFieldKeys.length && (fileField as any)?.files[0]) {
                    formData.append("file[" + currentIndex + "].file", (fileField as any)?.files[0]);
                    formData.append("file[" + currentIndex + "].formFieldKeyId", id);
                    currentIndex++
                }
            })
        }

        setIsSaving(true);
        formSectionService[params.actionId ? "updateOnFormActionSave" : "postFromData"](formData).then(
            (data) => {
                documentsToClear.forEach(d => {
                    var doc = document.getElementById(d);
                    (doc as any).value = null;
                });
                params.onSave();
            }, handleErrorResponseAxios)
            .finally(() => {
                setIsSaving(false)
            });
    }

    function valuetext(value: number) {
        return getMoneyFormatNoFractions(value);
    }

    function valuetextyears(value: number) {
        return value + "yrs";
    }
    function getField(field: IFormFieldKeyManage, index: number) {
        var validators = [];
        var validatorMessages = [];
        var isDisabled = params.isForReview;
        var variant = "outlined";

        if (field.isRequired) {
            validators.push("required");
            validatorMessages.push("This field is required");
        }

        switch (field.formFieldValueTypeId) {
            case (FormFieldValueTypeEnum.Slider):
                return <>
                    {(!!field.description && !params.actionId) &&
                        <Tooltip title={field.description}>
                            <IconButton disabled={isDisabled}>
                                <HelpCenterOutlined color="secondary" />
                            </IconButton>
                        </Tooltip>
                    }
                    <FormControl fullWidth>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>

                                <FormLabel
                                    sx={{ fontSize: "14px !important", margin: "2px", fontWeight: 'bold' }}
                                    id={field.label}> {!!params.firstName ? field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount ? params.firstName + ": " :
                                        field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmountJointLife ? params.firstNameJointLife + ": " : "" : ""}
                                    {field.subLabel}: {field.shortName === "term" || field.shortName === "nra" ?
                                        valuetextyears(Number(field.formField?.fieldValue || 0)).toString() :
                                        valuetext(Number(field.formField?.fieldValue || 0)).toString()
                                    }
                                </FormLabel>
                            </Grid>
                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    size="small"
                                    value={field.formField?.fieldValue ?? "0"}
                                    name={field.shortName}
                                    required={field.isRequired}
                                    fullWidth
                                    type={"number"}
                                    disabled={isDisabled}
                                    variant={variant}
                                    validators={field.isRequired ? ['required'] : []}
                                    errorMessages={field.isRequired ? ['This field is required'] : []}
                                    onChange={(e: any) => { if (!!field.maxValue && e.target.value > field.maxValue) { return; } handleChange(e, index) }}
                                    InputProps={{
                                        inputProps: {
                                            type: 'number',
                                            min: field.minValue,
                                            max: field.maxValue,
                                            step: field.shortName === "coveramount" || field.shortName === "coveramountjointlife" ? 10000 :
                                                field.shortName === "nra" ? 5 : 1
                                        },
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Slider sx={{
                                    //    color: 'grey'
                                }}
                                    aria-label="Custom marks"
                                    defaultValue={field.minValue || 0}
                                    value={Number(field.formField?.fieldValue || "")}
                                    onChange={(e: any) => { handleChange(e, index) }}
                                    getAriaValueText={field.shortName === "term" || field.shortName === "nra" ? valuetextyears : valuetext}
                                    step={field.shortName === "term" || field.shortName === "nra" ? 5 : 10000}
                                    min={field.minValue}
                                    max={field.maxValue}
                                    valueLabelDisplay="off"
                                    name={field.shortName}
                                    //marks={getSliderMarks(field.minValue, field.maxValue, field.shortName)}
                                    disabled={isDisabled}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography
                                        color="text.secondary"
                                        variant="body2"
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        {field.shortName === "term" || field.shortName === "nra" ? field.minValue + "years" : !!field.minValue ? getMoneyFormatNoFractions(field.minValue) : ""}
                                    </Typography>
                                    <Typography
                                        color="text.secondary"
                                        variant="body2"
                                    >
                                        {field.shortName === "term" || field.shortName === "nra" ? field.maxValue + " years" : !!field.maxValue ? getMoneyFormatNoFractions(field.maxValue) : ""}


                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </FormControl>
                </>
                break;
            case (FormFieldValueTypeEnum.Dropdown):
                return <>
                    <FormLabel
                        sx={{ fontSize: "14px !important", margin: "2px", fontWeight: 'bold' }}
                        id={field.label}>
                        {field.subLabel}: {field?.formField?.fieldValue}

                    </FormLabel>
                    {
                        !!field.description &&
                        <Tooltip title={field.description}>
                            <IconButton disabled={isDisabled}>
                                <Info color="info" />
                            </IconButton>
                        </Tooltip>
                    }
                    <Autocomplete
                        key={index}
                        options={field.formFieldKeyLookups!}
                        getOptionLabel={(option) => option.lookupValue}
                        onChange={(event, newValue) => {
                            handleAutocompleteChange(event, index, newValue?.lookupValue);
                        }}
                        value={field.formFieldKeyLookups?.find((opt) => opt.lookupValue === field.formField?.fieldValue || null)}
                        renderInput={(params) => <TextField {...params} size="small" value={field.formField?.fieldValue || ""} />}
                    />
                </>
                break
            //<SelectValidator size="small" sx={params.actionId ? { marginTop: "10px", height: '50px' } : {}}
            //    key={index}
            //    value={field.formField?.fieldValue ?? ""}
            //    onChange={(e: any) => { handleChange(e, index) }}
            //    required={field.isRequired}
            //    // label={field.label}
            //    name="formFieldValueTypeId"
            //    variant={variant}
            //    fullWidth
            //    disabled={isDisabled}
            //    validators={field.isRequired ? ['required'] : []}
            //    errorMessages={field.isRequired ? ['This field is required'] : []}
            //    SelectProps={{
            //        MenuProps: {
            //            disableScrollLock: true,
            //        },
            //    }}
            //    InputLabelProps={{
            //        shrink: !!field.formField?.fieldValue,
            //    }}>
            //    {field.formFieldKeyLookups?.map(lookup =>
            //        <MenuItem key={"list_item_menu_" + field.id + "_" + lookup.id} value={lookup.lookupValue}>{lookup.label || lookup.lookupValue}</MenuItem>
            //    )}
            //</SelectValidator>
            case (FormFieldValueTypeEnum.RadioButton):
                return <FormControl fullWidth>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <FormLabel
                                sx={{ fontSize: "14px !important", margin: "2px", fontWeight: 'bold' }}
                                id={field.label}>
                                {field.subLabel}
                            </FormLabel>
                        </Grid>
                        {field.formFieldKeyLookups?.map(lookup =>
                            <Grid item xs={!!params.actionId ? 12 : (field.formFieldKeyLookups?.length || 0) % 8 == 0 ? 3 : (field.formFieldKeyLookups?.length || 0) % 7 == 0 ? 3 : (field.formFieldKeyLookups?.length || 0) % 6 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 5 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 4 == 0 ? 3 : (field.formFieldKeyLookups?.length || 0) % 3 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 2 == 0 ? 6 : 12}>
                                {!!lookup.description &&
                                    <Tooltip title={lookup.description}>
                                        <IconButton disabled={isDisabled}>
                                            <Info color="info" />
                                        </IconButton>
                                    </Tooltip>
                                }
                                <ToggleButton size="small" disabled={isDisabled} fullWidth color="primary" onChange={(e: any) => { if (!e.target.value) { e.target.value = e.currentTarget.value; } handleChange(e, index) }} selected={!!field.formField?.fieldValue && field.formField?.fieldValue.toLowerCase() === lookup.lookupValue.toLowerCase()} value={lookup.lookupValue} aria-label="left aligned">
                                    <Typography variant="body2" textTransform="none">{lookup.lookupValue}</Typography>
                                </ToggleButton>
                            </Grid>
                        )}
                    </Grid>
                </FormControl>
                break;
            case (FormFieldValueTypeEnum.Number):
                return <Grid container spacing={1}>
                    <Grid item xs={12}>

                        <FormLabel
                            sx={{ fontSize: "14px !important", margin: "2px", fontWeight: 'bold' }}
                            id={field.label}>
                            {!!params.firstName ?
                                field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount ?
                                    params.firstName + ": " :
                                    field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmountJointLife ? params.firstNameJointLife + ": " : "" : ""}
                            {field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmountJointLife ? field.subLabel && field.subLabel.split("-")[0].trim() : field.subLabel}:
                            {field.shortName === "term" || field.shortName === "nra" ?
                                " " + valuetextyears(Number(field.formField?.fieldValue || 0)).toString() :
                                " " + valuetext(Number(field.formField?.fieldValue || 0)).toString()
                            }
                        </FormLabel>

                    </Grid>
                    <Grid item xs={12}>

                        {/*<TextValidatorHcs*/}
                        {/*    // label={field.label}*/}
                        {/*    size="small"*/}
                        {/*    value={field.formField?.fieldValue ?? ""}*/}
                        {/*    name={field.shortName}*/}
                        {/*    required={field.isRequired}*/}
                        {/*    fullWidth*/}
                        {/*    type={"number"}*/}
                        {/*    disabled={isDisabled}*/}
                        {/*    variant={variant}*/}
                        {/*    validators={field.isRequired ? ['required'] : []}*/}
                        {/*    errorMessages={field.isRequired ? ['This field is required'] : []}*/}
                        {/*    onChange={(e: any) => { handleChange(e, index) }}*/}
                        {/*/>*/}
                        <TextValidatorHcs

                            value={field.maxValue && Number(field.formField?.fieldValue) > (data?.formTemplateTypeId === FormTemplateTypeEnum.Mortgages && field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount &&
                                data?.formFieldKeys?.some((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income &&
                                    data.formFieldKeys!.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income)[0].formField?.fieldValue) ?
                                (Number(data?.formFieldKeys!.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income)[0].formField?.fieldValue) * 4) : field.maxValue) ?
                                field.formField?.fieldValue ?? "" : field.formField?.fieldValue ?? ""}
                            name={field.shortName}
                            required={field.isRequired}
                            fullWidth
                            type={"number"}
                            disabled={isDisabled}
                            variant={variant}
                            validators={field.isRequired ? ['required', 'isNumber', `minNumber:${field.minValue}`, `maxNumber:${field.maxValue}`,] :
                                ['isNumber', `minNumber:${field.minValue}`, `maxNumber:${field.maxValue}`]}
                            errorMessages={field.isRequired ? ['This field is required', 'Only numbers are allowed.',
                                `${field.label} must be minimum ${field.shortName === "coveramount" || field.shortName === "coveramountjointlife" ?
                                    getMoneyFormatNoFractions(field.minValue!) : field.shortName === "nra" ? field.minValue + "yrs" : field.minValue}`,
                                `${field.label} must be maximum ${field.shortName === "coveramount" || field.shortName === "coveramountjointlife" ?
                                    getMoneyFormatNoFractions(field.maxValue!) : field.shortName === "nra" ? field.maxValue + "yrs" : field.maxValue}`] :
                                ['Only numbers are allowed.', `${field.label} must be minimum ${getMoneyFormatNoFractions(field.minValue!)}`,
                                    `${field.label} must be maximum ${field.shortName === "coveramount" || field.shortName === "coveramountjointlife" ?
                                        getMoneyFormatNoFractions(field.maxValue!) : field.shortName === "nra" ? field.maxValue + "yrs" : field.maxValue}`]}
                            //onInput={handleInput}
                            onChange={(e: any) => { handleChange(e, index) }}
                            InputProps={{
                                inputProps: {
                                    step: field.shortName === "coveramount" || field.shortName === "coveramountjointlife" ? 10000 :
                                        field.shortName === "nra" ? 5 : 1
                                },
                                min: field.minValue,
                                //max: field.maxValue
                            }}
                            placeholder={field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount ||
                                field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmountJointLife ||
                                field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.PropertyValue ? "Example: 200000" :
                                field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Term ? "Example: 25" :
                                    field.label}
                        />


                    </Grid>
                </Grid>
                break;
            default: return ";";
        }
    }

    function isFormValid(e: any) {
        console.log("is form valud", e);
    }

    function checkIfDependancyMet(formFieldKey: IFormFieldKeyManage) {

        if ((!formFieldKey.formFieldKeyFormFieldKeyLookups || !formFieldKey.formFieldKeyFormFieldKeyLookups.length) && (!formFieldKey.formFieldKeyConditionDependencies || !formFieldKey.formFieldKeyConditionDependencies.length))
            return true;

        var _return: boolean | null = null;
        if (formFieldKey.formFieldKeyFormFieldKeyLookups && formFieldKey.formFieldKeyFormFieldKeyLookups.length) {

            formFieldKey.formFieldKeyFormFieldKeyLookups.forEach(ffkfl => {
                if (data && data.formFieldKeys && data.formFieldKeys) {

                    data.formFieldKeys.filter(ffk => ffk.formFieldKeyLookups).forEach(ffk => {
                        if (ffk.formFieldKeyLookups?.some(ffkl => ffkl.id == ffkfl.formFieldKeyLookupId)) {
                            if (ffk && ffk.formField && ffk.formField.fieldValue == ffkfl.value) _return = true;
                        }
                    });

                    var globalFormField = data?.globalFormFields?.find(m => m.formFieldKeyId == ffkfl.formFieldKeyLookupId);
                    if (globalFormField && globalFormField.fieldValue && globalFormField.fieldValue.toString().toLowerCase() === ffkfl.value.toString().toLowerCase()) {
                        _return = true;
                    }
                }
            });
        }

        if (formFieldKey.formFieldKeyConditionDependencies && formFieldKey.formFieldKeyConditionDependencies.length) {
            formFieldKey.formFieldKeyConditionDependencies.forEach((ffkcd: any) => {
                var globalFormField = data?.globalFormFields?.find(m => m.formFieldKeyId == ffkcd.conditionFormFieldKeyId);

                if (!globalFormField) {
                    _return = false;
                    return;
                }
                if (_return === null ||
                    (_return === true && formFieldKey.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.All) ||
                    (_return !== true && formFieldKey.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.Any)) {

                    if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsTo) {
                        _return = globalFormField.fieldValue.toString().toLowerCase() === ffkcd.value.toString().toLowerCase();
                    }
                    else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsOrGreater) {
                        _return = Number(globalFormField.fieldValue) >= Number(ffkcd.value);
                    }
                    else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsOrLess) {
                        _return = Number(globalFormField.fieldValue) <= Number(ffkcd.value);
                    }
                    else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Less) {
                        _return = Number(globalFormField.fieldValue) < Number(ffkcd.value);
                    }
                    else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Greater) {
                        _return = Number(globalFormField.fieldValue) > Number(ffkcd.value);
                    }
                    else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Contains) {
                        _return = globalFormField.fieldValue && ffkcd.value && globalFormField.fieldValue.trim().toLowerCase().includes(ffkcd.value.trim().toLowerCase());
                    }
                }


            });
        }

        if (_return === null)
            _return = false;

        return _return;
    }

    function getSections() {
        var subSections: any[] = [];
        if (data && data.formFieldKeys && data.formFieldKeys) {
            subSections = data.formFieldKeys.filter(ffk => ffk.formFieldValueTypeId === FormFieldValueTypeEnum.SubSection);
            if (data.formFieldKeys.some(ffk => ffk.formFieldValueTypeId !== FormFieldValueTypeEnum.SubSection && !ffk.subSectionId))
                subSections.push({});
        }

        return subSections || [{}];
    }

    function getSectionKeys(subSectionId?: any) {
        var keys: any[] = [];
        if (data && data.formFieldKeys && data.formFieldKeys) {
            return data.formFieldKeys.filter(ffk => ffk.formFieldValueTypeId !== FormFieldValueTypeEnum.SubSection && ffk.subSectionId == subSectionId)
        }

        return [];
    }

    return (
        <Grid container id="form-manage" key={"form_template_section_" + params.formTemplateSectionId}>
            <Grid item xs={12}>

                {(isLoading || isSaving || params.isSectionSaving) &&
                    <Paper sx={!!params.actionId ? { backgroundColor: 'transparent' } : { backgroundColor: 'auto' }} >
                        <Typography variant="body1" className="paper-theme-body" gutterBottom>
                            <LinearProgress color="secondary" />
                        </Typography>
                    </Paper>
                }
                <ValidatorForm isFormValid={isFormValid} onSubmit={submit} noValidate>

                    {getSections().filter(s =>
                        checkIfDependancyMet(s)).map((s, index) =>

                            <div key={"section_" + s.id + "_" + index}
                                style={{
                                    padding: '0px'
                                }}
                            >

                                {data && data.formTemplateTypeId === FormTemplateTypeEnum.Mortgages &&
                                    <Box display="flex" flexDirection="column" gap={2} marginBottom={1}>
                                        {getSectionKeys(s.id).filter(s => s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.QuoteResultsActionAdmin).filter(s =>
                                            showMoreFilters &&
                                                (screenSizeCategory === ScreenSizeCategoryEnum.XS || screenSizeCategory === ScreenSizeCategoryEnum.SM)
                                                ? (s.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount ||
                                                    s.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Term) && checkIfDependancyMet(s) :
                                                checkIfDependancyMet(s)).map((ffk, index) => <>
                                                    <Box
                                                        sx={{
                                                            padding: 1.5,
                                                            boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.25)}`,
                                                            //border: "0.5px solid grey",
                                                            borderRadius: '4px'
                                                        }} key={"subsection" + s.id + "_" + index + "_" + ffk.id}>
                                                        {getField(ffk, ffk.id)}
                                                    </Box>
                                                </>)}
                                        {index === 0 && (
                                            <Box
                                                display="flex"
                                                flexDirection={{ xs: "column", md: "row" }}
                                                gap={1}
                                            >
                                                {(screenSizeCategory === ScreenSizeCategoryEnum.XS ||
                                                    screenSizeCategory === ScreenSizeCategoryEnum.SM) && (
                                                        <Button
                                                            fullWidth
                                                            variant="soft"
                                                            color="secondary"
                                                            type="button"
                                                            onClick={() => setShowMoreFilters((prev: any) => !prev)}
                                                            startIcon={showMoreFilters ? <Add /> : <Remove />}
                                                            sx={{
                                                                width: "100% !important",
                                                                borderRadius: 1,
                                                                textTransform: "none",
                                                            }}
                                                        >
                                                            <Typography variant="subtitle1">
                                                                {showMoreFilters ? "More Filters" : "Less Filters"}
                                                            </Typography>
                                                        </Button>
                                                    )}

                                                <Button
                                                    sx={{ width: "100% !important" }}
                                                    variant="contained"
                                                    color="secondary"
                                                    type="submit"
                                                    fullWidth
                                                    startIcon={<Autorenew />}
                                                >
                                                    <Typography variant="subtitle1">Get Another Quote</Typography>
                                                </Button>
                                            </Box>

                                        )}
                                    </Box>
                                }
                                {data && data.formTemplateTypeId !== FormTemplateTypeEnum.Mortgages &&
                                    <Container maxWidth="md" sx={{
                                        paddingTop: { xs: "4px !important", md: "12px !important" },
                                        paddingLeft: { xs: '4px !important', md: '10px !important' },
                                        paddingRight: { xs: '4px !important', md: '10px !important' }
                                    }}>
                                        <Box display="flex" flexDirection={{ xs: "column", md: "row" }} width="100%" gap={2} marginBottom={1}>
                                            {getSectionKeys(s.id).filter(s => s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.IndexationAdmin
                                                && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.SeriousIllnessCoverTypeAdmin
                                                && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.SeriousIllnessCoverAmountAdmin
                                                && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.SeriousIllnessCoverAmountJointLifeAdmin
                                                && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.ConvertibleOptionAdmin
                                                && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.QuoteResultsActionAdmin
                                                && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.IncomeProtectionCoverAmountAdmin
                                                && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.IncomeProtectionCoverAmountAvailable
                                                && checkIfDependancyMet(s)).map((ffk, index) => <>

                                                    <Box
                                                        key={"subsection" + s.id + "_" + index + "_" + ffk.id}
                                                        flex={1}
                                                        sx={{
                                                            padding: 1.5,
                                                            boxShadow: 1,
                                                            borderRadius: "4px",
                                                            backgroundColor: "white"
                                                        }}
                                                    >
                                                        {getField(ffk, ffk.id)}
                                                    </Box>
                                                </>)}

                                        </Box>
                                        {index === 0 && (
                                            <Box display="flex" mb={1}>
                                                <Button
                                                    sx={{ width: "100% !important" }}
                                                    variant="contained"
                                                    color="secondary"
                                                    type="submit"
                                                    startIcon={<Autorenew />}
                                                >
                                                    <Typography variant="subtitle1">Get Another Quote</Typography>
                                                </Button>
                                            </Box>
                                        )}
                                    </Container>
                                }

                            </div>
                        )}


                </ValidatorForm>

            </Grid>

        </Grid>
    );
}